import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { useInstrumentSetupOptional } from "../../../contexts/InstrumentSetupContext";

import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "../../../components/Strategies/StrategyPineSectionCard";
import { WorkspaceWizardAutoHeight } from "./WorkspaceWizardAutoHeight";

export const WorkspaceWizardFooterSlotContext = React.createContext<HTMLElement | null>(null);

export function useWorkspaceWizardFooterSlot(): HTMLElement | null {
  return React.useContext(WorkspaceWizardFooterSlotContext);
}

/**
 * Shared size for hub create wizards (strategy / report / simulation)
 * so the three dialogs feel like one family.
 */
export const WORKSPACE_WIZARD_DIALOG_SIZE_CLASS =
  "h-auto max-h-[min(92vh,820px)] w-[min(96vw,720px)] sm:max-w-[720px]";

/** Wider shell for Run simulation so the policy graph can fit without extreme zoom. */
export const WORKSPACE_SIMULATOR_WIZARD_DIALOG_SIZE_CLASS =
  "h-auto max-h-[min(92vh,900px)] w-[min(96vw,960px)] sm:max-w-[960px]";

export type WorkspaceWizardDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  /** Override the body region (e.g. flex column for stepped wizards with their own footer). */
  bodyClassName?: string;
  /** Hide the visible title/description row (sr-only labels remain for a11y). */
  hideIntro?: boolean;
  /** Smoothly animate dialog body height as content grows/shrinks. */
  animateHeight?: boolean;
  /**
   * When true, exposes a footer mount point below the height-animated body so footers
   * stay glued to the dialog bottom during height transitions.
   */
  pinFooterSlot?: boolean;
};

export function WorkspaceWizardDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  bodyClassName,
  hideIntro = false,
  animateHeight = false,
  pinFooterSlot = false,
}: WorkspaceWizardDialogProps): React.ReactElement {
  const [footerSlotEl, setFooterSlotEl] = React.useState<HTMLDivElement | null>(null);
  const instrumentSetup = useInstrumentSetupOptional();
  const instrumentSetupOpen = Boolean(instrumentSetup?.isOpen);

  const main = (
    <>
      <DialogHeader
        className={cn(
          "shrink-0 text-left",
          hideIntro ? "sr-only" : "border-b border-border px-6 py-4 dark:border-input"
        )}
      >
        <DialogTitle className={hideIntro ? undefined : "sr-only"}>{title}</DialogTitle>
        {description ? (
          <DialogDescription className={hideIntro ? undefined : "sr-only"}>
            {description}
          </DialogDescription>
        ) : null}
        {hideIntro ? null : (
          <Field data-workspace-wizard-dialog-intro="">
            <FieldLabel className={cn(STRATEGY_PINE_SECTION_LABEL_CLASS, "text-lg leading-snug")}>
              {title}
            </FieldLabel>
            {description ? (
              <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                {description}
              </FieldDescription>
            ) : null}
          </Field>
        )}
      </DialogHeader>
      <div className={cn("flex flex-col", bodyClassName ?? "overflow-y-auto px-6 py-4")}>
        {children}
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-workspace-wizard-dialog=""
        forceMount={open ? true : undefined}
        onOpenAutoFocus={(event) => {
          // Avoid focusing Continue/Generate under the click that opened the dialog
          // (pointer fall-through would otherwise advance/run immediately).
          event.preventDefault();
        }}
        className={cn(
          // DialogContent defaults to sm:max-w-sm — must override with sm: prefix.
          "flex max-h-[min(92vh,920px)] w-[min(96vw,960px)] max-w-[calc(100%-2rem)] sm:max-w-none flex-col gap-0 overflow-hidden p-0",
          animateHeight && "transition-none",
          instrumentSetupOpen && "!hidden",
          className
        )}
      >
        <WorkspaceWizardFooterSlotContext.Provider value={pinFooterSlot ? footerSlotEl : null}>
          {animateHeight ? (
            <WorkspaceWizardAutoHeight
              className="flex w-full min-h-0 flex-col"
              bottomReservePx={pinFooterSlot ? 72 : 0}
            >
              {main}
            </WorkspaceWizardAutoHeight>
          ) : (
            main
          )}
          {pinFooterSlot ? (
            <div
              ref={setFooterSlotEl}
              className="shrink-0 bg-muted"
              data-workspace-wizard-footer-slot=""
            />
          ) : null}
        </WorkspaceWizardFooterSlotContext.Provider>
      </DialogContent>
    </Dialog>
  );
}
